import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  OnChanges,
  OnInit,
  Renderer2,
  Input,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  EmailInvalid,
  FieldRequired,
  GenericMessage,
  MaxLength,
  MinLength,
  PasswordInvalid,
  PhoneInvalid,
  LinkInvalid,
  OnlyLettersInvalid,
  PhoneFormat,
  OnlyNumbersInvalid,
} from '@config/messages.config';
import {
  RegexEmail,
  RegexOnlyLetters,
  RegexOnlyNumbers,
  RegexPassword,
  RegexPhone,
  RegexUrl,
} from '@config/regex.config';

@Directive({
  selector: '[ggiJourneyControlValidator]',
})
export class ControlValidatorDirective implements OnInit, OnChanges {
  @Input() submitted = false;

  private readonly prefixId = 'directive-ggi-validator';

  private currentId: string;

  constructor(
    private readonly el: ElementRef,
    private readonly control: NgControl,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  ngOnInit(): void {
    this.currentId = `${this.prefixId}-${this.el.nativeElement.id}`;

    const errorElement = this.document.createElement('p');
    errorElement.classList.add('text-small');
    errorElement.classList.add('error');

    errorElement.id = this.currentId;

    this.renderer.appendChild(this.el.nativeElement.parentNode, errorElement);
  }

  ngOnChanges(): void {
    if (this.submitted) {
      this.setErrorMessage(this.control);
    }
  }

  @HostListener('input', ['$event']) onEvent(): void {
    this.setErrorMessage(this.control);
  }

  @HostListener('blur', ['$event']) onBlur(): void {
    this.setErrorMessage(this.control);
  }

  private setErrorMessage({ errors }: NgControl): void {
    const setMessage = (msg: string = ''): void => {
      if (this.document.getElementById(this.currentId)) {
        this.document.getElementById(this.currentId).innerHTML = msg;
      }
    };

    if (!errors) {
      setMessage();
      return;
    }

    for (const key in errors) {
      if (Object.prototype.hasOwnProperty.call(errors, key)) {
        const error = errors[key];
        switch (key) {
          case TypeFieldErrors.required:
            setMessage(FieldRequired);
            break;
          case TypeFieldErrors.phoneFormat:
            setMessage(PhoneFormat);
            break;
          case TypeFieldErrors.minlength:
            setMessage(MinLength.replace('{length}', error.requiredLength));
            break;
          case TypeFieldErrors.maxlength:
            setMessage(MaxLength.replace('{length}', error.requiredLength));
            break;
          case TypeFieldErrors.pattern:
            const msg = this.searchPattern(error.requiredPattern);
            setMessage(msg);
            break;
          default:
            setMessage(GenericMessage);
            break;
        }
      }
    }
  }

  private searchPattern(requiredPattern: string): string {
    const patterns = [
      RegexEmail,
      RegexPhone,
      RegexPassword,
      RegexUrl,
      RegexOnlyLetters,
      RegexOnlyNumbers,
    ];

    const idx = patterns.findIndex(
      (e) => requiredPattern.indexOf(e.toString()) !== -1
    );

    if (idx === undefined || idx === null) {
      return GenericMessage;
    }

    switch (idx) {
      case TypePatternExists.email:
        return EmailInvalid;
      case TypePatternExists.phone:
        return PhoneInvalid;
      case TypePatternExists.password:
        return PasswordInvalid;
      case TypePatternExists.link:
        return LinkInvalid;
      case TypePatternExists.onlyLetters:
        return OnlyLettersInvalid;
      case TypePatternExists.onlyNumbers:
        return OnlyNumbersInvalid;
      default:
        return GenericMessage;
    }
  }
}

enum TypeFieldErrors {
  required = 'required',
  pattern = 'pattern',
  minlength = 'minlength',
  maxlength = 'maxlength',
  phoneFormat = 'phoneFormat',
}

enum TypePatternExists {
  email,
  phone,
  password,
  link,
  onlyLetters,
  onlyNumbers,
}
